import { format, parse } from 'date-fns';
import { observer } from 'mobx-react';
import * as React from 'react';
import {
  AnalyticsTrigger,
  Anchor,
  Heading,
  List,
  ListItem,
  Pagination,
  Separator,
  Tag,
  Text,
  THeadingLevel,
  Grid,
  GridColumn,
  Skeleton,
  SkeletonItem
} from 'telia-front-react';
import NotFound from '../notfound/NotFound';
import { languageSlug } from '../stores/LanguageStore';
import { TranslationsContext } from '@teliaee/telia-translate';
import { useStores } from '../stores/RootStore';

export interface NewsListProps {
  perPage?: number;
  category?: string;
  tag?: string;
  rootPath?: string;
  dateFormat?: string;
  title?: string;
  headingLevel?: THeadingLevel;
}

const NewsList = (props: NewsListProps): React.JSX.Element => {
  const { dateFormat, tag, perPage = 5, rootPath, category, title, headingLevel = 1 } = props;
  const {
    newsStore: { loadNews, currentPage, getNewsItemsForPage, getTotalPages, setCurrentPage, newsNotFound, areCurrentPageNewsItemsFetching },
    languageStore: { currentLanguage, currentLocale }
  } = useStores();
  const { translate } = React.useContext(TranslationsContext);
  const titleLabel = title || translate('news.title');
  const resolvedDateFormat = dateFormat || 'd. MMMM yyyy';
  const resolvedRootPath = rootPath || '/uudised/';
  const newsItems = getNewsItemsForPage(perPage, tag, category);
  const totalPages = getTotalPages(perPage, tag, category);

  React.useEffect(() => {
    loadNews(perPage, tag, category);
  }, []);

  const paginationChange = (page: number) => {
    setCurrentPage(page);
    loadNews(perPage, tag, category);
  };

  if (newsNotFound(tag, category)) {
    return <NotFound />;
  }

  return (
    <Text>
      <Heading level={headingLevel} className="h2">
        {titleLabel}
      </Heading>
      {areCurrentPageNewsItemsFetching(tag, category) ? (
        <Skeleton label={translate('skeleton.loading')}>
          <SkeletonItem height={`${49.5 * perPage}px`} width="70%" />
        </Skeleton>
      ) : (
        <List type="unstyled">
          {newsItems.map((item, index) => (
            <ListItem key={index}>
              <Grid className="text-small" gutter="xsmall">
                <GridColumn width={['xs']}>
                  <time dateTime={item.date}> {format(parse(item.date, 'dd.MM.yyyy', Date.now()), resolvedDateFormat, { locale: currentLocale })}</time>
                </GridColumn>
                <GridColumn width={['xs']}>{!!item?.tags?.length && item.tags.map((t, idx) => <Tag key={`${t}-${idx}`} text={t} size="tiny" />)}</GridColumn>
              </Grid>
              <AnalyticsTrigger trigger={{ id: `Link - ${item.title}`, callback: 'onClick' }}>
                <Anchor isTextColor={true} url={`${languageSlug(currentLanguage)}${resolvedRootPath}${item.slug}`}>
                  {item.title}
                </Anchor>
              </AnalyticsTrigger>
            </ListItem>
          ))}
        </List>
      )}
      {totalPages !== 0 && (
        <>
          <Separator spacing="small" />
          <Pagination pages={totalPages} currentPage={currentPage} position="left" onChange={paginationChange} />
        </>
      )}
    </Text>
  );
};

export default observer(NewsList);
